/**
 * Public service endpoints the documentation links to and the interactive
 * playgrounds call. Each value reads a Vite build-time override (`VITE_*`) and
 * falls back to the production host, so a deploy can retarget the playgrounds at
 * a staging environment without code changes while plain `vite build` ships the
 * production hosts.
 */

/**
 * DATA_API_URL is the GraphQL Data API origin. The GraphiQL playground executes
 * queries against `${DATA_API_URL}/graphql`; the schema explorer itself loads
 * from a static SDL and needs no network access to this host.
 */
export const DATA_API_URL: string =
  import.meta.env.VITE_HTTP_DATA_API_URL ?? "https://data.mountaya.com";

/**
 * ROOT_DOMAIN is the application root origin that serves the hosted embed. The
 * embed frame builds its iframe `src` as `${ROOT_DOMAIN}/studio/embed?…`.
 */
export const ROOT_DOMAIN: string = import.meta.env.VITE_ROOT_DOMAIN ?? "https://app.mountaya.com";

/**
 * INTERNAL_API_URL is the Map Studio API origin. The session-minting flow
 * (`POST /v1/sessions`) lives here, and it is what the live element previews hand
 * their client as the `rest` base URL, for the reads they make beside the tiles.
 *
 * The fallback is `api.mountaya.com`, which is now both the documented origin and
 * the host answering on it. It named `internal.mountaya.com` through the migration,
 * because a fallback pointing at a destination that does not resolve yet is what
 * left the map previews with no layers manifest (then read from this origin) and
 * therefore no terrain. The old
 * name no longer serves a usable certificate, so naming it now fails the same reads
 * the same silent way, in the other direction. This agrees with the SDK's own
 * `defaultBaseUrls.rest`, which is the value a reader gets with no configuration.
 */
export const INTERNAL_API_URL: string =
  import.meta.env.VITE_HTTP_INTERNAL_API_URL ?? "https://api.mountaya.com";

/**
 * TILE_API_URL is the Tile API origin serving overlay specifications, raster tiles
 * and the layers manifest (`GET /v1/tile-layers`) the live element previews resolve
 * a terrain from. The tile-integration snippets reference it.
 */
export const TILE_API_URL: string =
  import.meta.env.VITE_HTTP_TILE_API_URL ?? "https://tiles.mountaya.com";

/**
 * GOOGLE_MAPS_API_KEY is what `StreetViewControl` opens a panorama with.
 *
 * Unlike everything above it, this has no fallback and is expected to be empty: the imagery
 * is Google's, so the key belongs to whoever renders the control rather than to Mountaya.
 * The control mounts and drags either way and reports through `onUnavailable` when there is
 * no panorama to open, which is the same path it takes at a spot Google has never driven —
 * so the demo is honest with the key absent, and richer with it present.
 */
export const GOOGLE_MAPS_API_KEY: string = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "";

/**
 * DOCS_ORIGIN is the public origin the documentation site is served from. The
 * prerender step builds absolute URLs from it for the machine-readable discovery
 * surface — the canonical links and Open Graph tags in each page's head, the
 * sitemap, the `llms.txt` index, and the per-page `.md` links it references — and
 * the in-page "copy for LLM" control uses it to hand a reader an absolute page
 * URL. A `VITE_*` override retargets a preview deploy without code changes.
 */
export const DOCS_ORIGIN: string = import.meta.env.VITE_DOCS_ORIGIN ?? "https://docs.mountaya.com";

/**
 * POSTHOG_HOST is the analytics ingest origin. It is the same reverse proxy the
 * marketing site and the application post to, so a reader's anonymous id carries
 * across the parent domain and an ad blocker sees a first-party host.
 */
export const POSTHOG_HOST: string = import.meta.env.VITE_POSTHOG_HOST ?? "https://t.mountaya.com";

/**
 * POSTHOG_UI_HOST is the PostHog application itself. Ingest goes through the
 * proxy above, so the SDK is told the real origin separately or the links it
 * renders would point back at the proxy.
 */
export const POSTHOG_UI_HOST = "https://eu.posthog.com";

/**
 * POSTHOG_PROJECT_TOKEN is the analytics project token. Unlike the endpoints
 * above it carries no production fallback: a token is environment configuration
 * the deploy supplies, and an unset value simply leaves analytics off rather than
 * reporting a local or preview build into the production project.
 */
export const POSTHOG_PROJECT_TOKEN: string = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN ?? "";
